/**
 * Evidence → skill inference. Pure, deterministic, no AI, no randomness.
 *
 * The rule: level comes from accumulated evidence weight, confidence comes
 * from how *many different ways* the evidence arrived. A single checklist
 * tick can never produce "advanced" — corroboration must come from a
 * different item and, ideally, a different source (an artefact, an activity,
 * a mentor review), never a clone of the same question.
 */

import { EVIDENCE_ITEMS } from "./items";
import { skill } from "./catalogue";
import type {
  EvidenceRecord,
  InferredSkill,
  SkillLevel,
} from "./types";

const ITEM_INDEX = new Map(EVIDENCE_ITEMS.map((i) => [i.item_id, i]));

/** Saturating curve: weight 3 ≈ 45, 8 ≈ 76, 14 ≈ 91. Never reaches 100. */
function saturate(weight: number): number {
  return Math.round(100 * (1 - Math.exp(-weight / 5)));
}

function levelFor(score: number, confidence: number): SkillLevel {
  // Low confidence caps the claim. We would rather understate a skill than
  // put a level on a report that one tick-box produced.
  const cap: SkillLevel = confidence < 0.5 ? "novice" : confidence < 0.75 ? "intermediate" : "expert";
  const raw: SkillLevel =
    score >= 88 ? "expert"
    : score >= 70 ? "advanced"
    : score >= 45 ? "intermediate"
    : score >= 20 ? "novice"
    : "aware";
  const order: SkillLevel[] = ["aware", "novice", "intermediate", "advanced", "expert"];
  return order.indexOf(raw) > order.indexOf(cap) ? cap : raw;
}

/**
 * Confidence = breadth (distinct items) × source diversity, capped at 1.
 *  1 item, 1 source            → 0.49  (level capped at novice)
 *  2 items, 1 source           → 0.66  (capped at intermediate)
 *  3+ items, 2+ sources        → 1.00
 */
function confidenceFor(items: number, sources: number): number {
  const breadth = Math.min(1, items / 3);
  const diversity = Math.min(1, sources / 2);
  return Math.round(Math.min(1, 0.15 + 0.5 * breadth + 0.35 * diversity) * 100) / 100;
}

export function inferSkills(records: EvidenceRecord[]): InferredSkill[] {
  type Acc = { weight: number; items: Set<string>; sources: Set<string>; evidence: string[] };
  const acc = new Map<string, Acc>();

  // Stable ordering: by item id, then option order as declared in the bank.
  const ordered = [...records].sort((a, b) => a.item_id.localeCompare(b.item_id));

  for (const rec of ordered) {
    const item = ITEM_INDEX.get(rec.item_id);
    if (!item) continue;
    for (const opt of item.options) {
      if (!rec.option_ids.includes(opt.option_id)) continue;
      if (opt.weight <= 0 || opt.skills.length === 0) continue;
      for (const skill_id of opt.skills) {
        let a = acc.get(skill_id);
        if (!a) {
          a = { weight: 0, items: new Set(), sources: new Set(), evidence: [] };
          acc.set(skill_id, a);
        }
        // An artefact or activity source is stronger evidence than a claim.
        const sourceBoost = rec.source === "document" || rec.source === "activity" ? 1.25
          : rec.source === "mentor" ? 1.5
          : 1;
        a.weight += opt.weight * sourceBoost;
        a.items.add(rec.item_id);
        a.sources.add(rec.source);
        if (!a.evidence.includes(opt.label)) a.evidence.push(opt.label);
      }
    }
  }

  const out: InferredSkill[] = [];
  for (const [skill_id, a] of acc) {
    const node = skill(skill_id);
    if (!node) continue;
    const level_score = saturate(a.weight);
    const confidence = confidenceFor(a.items.size, a.sources.size);
    out.push({
      skill_id,
      label: node.label,
      domain: node.domain,
      level: levelFor(level_score, confidence),
      level_score,
      confidence,
      evidence: a.evidence.slice(0, 6),
      taxonomy: node.taxonomy,
    });
  }

  // Deterministic: strongest first, then alphabetical on ties.
  return out.sort(
    (x, y) => y.level_score - x.level_score || x.skill_id.localeCompare(y.skill_id),
  );
}

/** Mean level_score across a set of skill ids, or null when none has evidence. */
export function domainStrength(skills: InferredSkill[], ids: string[]): number | null {
  const hits = skills.filter((s) => ids.includes(s.skill_id));
  if (hits.length === 0) return null;
  const sum = hits.reduce((t, s) => t + s.level_score, 0);
  return Math.round(sum / hits.length);
}
